/**
 * TRL Round Card Component
 *
 * Trailblazers-specific round card:
 * - Screenshot preview (name, area, thumbnail)
 * - Search duration, capture/close radii, points scale
 * - Simple player list
 * - Standard lifecycle actions (Load/Unload/Start/End/Cancel/Disable/Delete/Edit)
 */

import { memo } from 'react';
import {
  Round,
  Player,
  isTrlSettings,
  TrlRoundSettings,
  canAdminDo,
  GameType,
  DEFAULT_TRL_BASE_POINTS,
  DEFAULT_TRL_POINTS_DECREMENT,
  type GameStatus,
} from '@swtorbit/shared-socket-events';
import { getRoundStatusColor } from '@swtorbit/shared-react-hooks';
import { API_URL } from '../../config';
import { useGameContext } from '../../contexts/GameContext';
import { RoundCardBase, RoundActionsBar } from '../round';
import { TrlPlayerItem } from './TrlPlayerItem';

/** Type-safe helper to get TRL settings from a round. */
function getTrlSettings(round: Round): TrlRoundSettings | undefined {
  return isTrlSettings(round.settings) ? round.settings : undefined;
}

export interface TrlRoundCardProps {
  round: Round;
  index: number;
  hasActiveRound: boolean;
  isLoaded: boolean;
  gamePlayers: Player[];
  onStart: (index: number) => void;
  onEnd: (index: number) => void;
  onCancel: (index: number) => void;
  onDisable: (index: number) => void;
  onDelete: (index: number) => void;
  onEdit: (index: number) => void;
  onLoad: (index: number) => void;
  onUnload: (index: number) => void;
}

export const TrlRoundCard = memo(function TrlRoundCard({
  round,
  index,
  hasActiveRound,
  isLoaded,
  gamePlayers,
  onStart,
  onEnd,
  onCancel,
  onDisable,
  onDelete,
  onEdit,
  onLoad,
  onUnload,
}: TrlRoundCardProps) {
  const { selectedGame } = useGameContext();

  const settings = getTrlSettings(round);
  if (!settings) {
    return <div className="card card--sm card--bg-midnight card--border card--border-navy">Invalid TRL round settings</div>;
  }

  const isSearchActive = round.status === 'searching';
  const canEnd = isSearchActive;

  // Derive game-lifecycle-aware button visibility
  const gameStatus = selectedGame?.status as GameStatus | undefined;
  const canLoad =
    gameStatus !== undefined &&
    canAdminDo(GameType.TRAILBLAZERS, gameStatus, 'canLoadRound') &&
    round.status === 'setup' &&
    !isLoaded;
  const canUnloadOrStart =
    gameStatus !== undefined &&
    canAdminDo(GameType.TRAILBLAZERS, gameStatus, 'canLoadRound') &&
    round.status === 'setup' &&
    isLoaded;
  const canDelete = round.status === 'setup' && !isLoaded;
  const canEdit = round.status === 'setup' && !isLoaded;
  const canDisable = round.status === 'completed';

  const basePoints = settings.basePoints ?? DEFAULT_TRL_BASE_POINTS;
  const pointsDecrement = settings.pointsDecrement ?? DEFAULT_TRL_POINTS_DECREMENT;

  return (
    <RoundCardBase
      index={index}
      status={round.status}
      disabled={round.disabled}
      borderColor={getRoundStatusColor(round.status)}
      actions={
        <RoundActionsBar
          leftActions={
            <>
              {canDelete && (
                <button onClick={() => onDelete(index)} className="btn btn--red">
                  🗑️ Delete
                </button>
              )}
              {canEdit && (
                <button onClick={() => onEdit(index)} className="btn btn--aqua">
                  ✏️ Edit
                </button>
              )}
              {canDisable && (
                <button
                  onClick={() => onDisable(index)}
                  className={`btn ${round.disabled ? 'btn--green' : 'btn--amber'}`}
                >
                  {round.disabled ? '✅ Enable' : '🚫 Disable'}
                </button>
              )}
            </>
          }
          rightActions={
            <>
              {canLoad && (
                <button onClick={() => onLoad(index)} className="btn btn--aqua">📥 Load</button>
              )}
              {canUnloadOrStart && (
                <button onClick={() => onUnload(index)} className="btn btn--outline btn--slate">📤 Unload</button>
              )}
              {canUnloadOrStart && !hasActiveRound && (
                <button onClick={() => onStart(index)} className="btn btn--green">🚩 Start Round</button>
              )}
              {canUnloadOrStart && hasActiveRound && (
                <button disabled className="btn btn--ghost" title="Another round is active">🚩 Start Round (Blocked)</button>
              )}
              {canEnd && (
                <>
                  <button onClick={() => onEnd(index)} className="btn">🏆 End Round</button>
                  <button onClick={() => onCancel(index)} className="btn btn--red">❌ Cancel</button>
                </>
              )}
            </>
          }
        />
      }
    >
      {/* Screenshot preview */}
      <div className="d-flex gap-sm align-items-center flex-wrap">
        {settings.imageUrl && (
          <img
            src={`${API_URL}${settings.imageUrl}`}
            alt={settings.imageName ?? 'Screenshot'}
            className="rounded-sm border-md border-blue-light"
            style={{ width: 160, height: 90, objectFit: 'cover' }}
          />
        )}
        <div className="d-flex flex-column gap-2xs">
          <span className="fw-bold fs-lg">📷 {settings.imageName ?? 'Screenshot'}</span>
          {settings.imageArea && <span className="fs-sm text-slate">{settings.imageArea}</span>}
        </div>
      </div>

      {/* Settings summary */}
      <div className="d-flex flex-wrap gap-xs mt-xs">
        <span className="badge">
          ⏱ {settings.searchDuration === 0 ? 'Unlimited' : `${settings.searchDuration}s`}
        </span>
        <span className="badge">🏅 {basePoints} pts (−{pointsDecrement}/rank)</span>
      </div>

      <p className="fw-bold mt-xs">Players ({round.players.length}):</p>
      <div className="d-flex flex-column gap-2xs">
        {round.players.map((player) => (
          <TrlPlayerItem key={player.playerId} player={player} gamePlayers={gamePlayers} />
        ))}
      </div>
    </RoundCardBase>
  );
});
